import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone,
  Email,
  LocationOn,
  Language,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navbarVariants } from '../styles/animations';
import GetCookie from '../Services/GetCookie';
import Data from '../Data/.json';
import logo from '../LogoFont.png';

interface NavItem {
  label: string;
  path: string;
  hasDropdown?: boolean;
  dropdownItems?: { label: string; path: string }[];
}

const ModernNavbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  
  const cookieMode = GetCookie(Data.mode);
  
  const navItems: NavItem[] = [
    { label: t("accueil"), path: '/' },
    { label: t("apropos"), path: '/about' },
    { label: t("services"), path: '/services' },
    { label: t("projets"), path: '/projects' },
    { label: t("contactM"), path: '/contact' },
    { label: t("carriere"), path: '/career' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setAnchorEl(null);
  };

  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        background: cookieMode === 'dark' ? '#0a0e27' : '#ffffff',
        color: cookieMode === 'dark' ? '#ffffff' : '#1a1a1a',
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Avatar
          src={logo}
          sx={{ width: 60, height: 60 }}
          onClick={() => handleNavigation('/')}
        />
        <IconButton onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      <List sx={{ p: 2 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.path}
            button
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&.Mui-selected': {
                backgroundColor: cookieMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(26,35,126,0.1)',
              },
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
      
      <Divider />
      
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Language />
          </IconButton>
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Phone sx={{ fontSize: 16, mr: 1 }} />
            {t("telephone")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Email sx={{ fontSize: 16, mr: 1 }} />
            {t("email")}
          </Typography>
          <Typography variant="body2">
            <LocationOn sx={{ fontSize: 16, mr: 1 }} />
            {t("adresse")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled
            ? cookieMode === 'dark'
              ? 'rgba(10, 14, 39, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
          py: 1,
        }}
        component={motion.div}
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleNavigation('/')}
              style={{ cursor: 'pointer' }}
            >
              <Avatar
                src={logo}
                sx={{
                  width: isMobile ? 40 : 50,
                  height: isMobile ? 40 : 50,
                  border: scrolled ? '2px solid' : '2px solid transparent',
                  borderColor: 'primary.main',
                }}
              />
            </motion.div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navItems.map((item) => (
                  <motion.div
                    key={item.path}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        color: 'primary.main',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        borderBottom: location.pathname === item.path ? '2px solid' : 'none',
                        borderColor: 'primary.main',
                        borderRadius: 0,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: 'primary.main',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  </motion.div>
                ))}
                
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ color: 'primary.main' }}>
                  <Language />
                </IconButton>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: 'primary.main',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            background: cookieMode === 'dark' ? '#0a0e27' : '#ffffff',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Language Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 120,
          },
        }}
      >
        <MenuItem onClick={() => handleLanguageChange('fr')}>Français</MenuItem>
        <MenuItem onClick={() => handleLanguageChange('en')}>English</MenuItem>
      </Menu>

      {/* Toolbar Spacer */}
      <Toolbar />
    </>
  );
};

export default ModernNavbar;
